import React, { useState, useEffect } from 'react';
import { Plus, Search, Printer } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import ProductTable from '../components/admin/ProductTable';
import ProductDialog from '../components/admin/ProductDialog';
import InventoryDialog from '../components/admin/InventoryDialog';
import { productApi, categoryApi } from '../api';

const ProductsPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];
  const isHQ = currentUser?.store_type === 'headquarters';

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [filterStock, setFilterStock] = useState('');

  const [categoryTree, setCategoryTree] = useState([]);
  const [filterParent, setFilterParent] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await productApi.getList({ status: 'active' });
      if (res.success) {
        setProducts(res.data);
        setFilteredProducts(res.data);
      } else {
        setError(res.message || '获取商品失败');
      }
    } catch (err) {
      setError(err.message || '获取商品失败');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await categoryApi.getList();
      if (res.success) setCategoryTree(res.data || []);
    } catch (err) { console.error('加载分类失败:', err); }
  };

  useEffect(() => { loadProducts(); loadCategories(); }, []);

  useEffect(() => {
    let list = products;

    if (filterCategory) {
      list = list.filter(p => p.category_id === parseInt(filterCategory));
    } else if (filterParent) {
      const parent = categoryTree.find(p => p.id === parseInt(filterParent));
      if (parent) {
        const childIds = (parent.children || []).map(c => c.id);
        list = list.filter(p => childIds.includes(p.category_id));
      }
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(term) ||
        p.name_en?.toLowerCase().includes(term) ||
        p.name_km?.toLowerCase().includes(term) ||
        p.barcode?.includes(term)
      );
    }

    if (filterStock === 'out') {
      list = list.filter(p => (p.stock || 0) === 0);
    } else if (filterStock === 'low') {
      list = list.filter(p => (p.stock || 0) > 0 && (p.stock || 0) <= (p.min_stock || 0));
    } else if (filterStock === 'in') {
      list = list.filter(p => (p.stock || 0) > (p.min_stock || 0));
    }

    setFilteredProducts(list);
  }, [searchTerm, filterParent, filterCategory, filterStock, products, categoryTree]);

  const handleParentFilter = (parentId) => {
    setFilterParent(parentId);
    setFilterCategory('');
  };

  const currentFilterChildren = filterParent
    ? (categoryTree.find(p => p.id === parseInt(filterParent))?.children || [])
    : [];

  const getCategoryName = (cat) => {
    if (language === 'en') return cat.name_en || cat.name;
    if (language === 'km') return cat.name_km || cat.name;
    return cat.name;
  };

  const handleAdd = () => { setEditingProduct(null); setShowDialog(true); };
  const handleEdit = (product) => { setEditingProduct(product); setShowDialog(true); };

  const handleSubmit = async (productData) => {
    try {
      let res;
      if (editingProduct) {
        res = await productApi.update(editingProduct.id, productData);
      } else {
        res = await productApi.create(productData);
      }
      if (res.success) {
        loadProducts();
        setShowDialog(false);
        setEditingProduct(null);
        alert(editingProduct ? '商品更新成功！' : '商品添加成功！');
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.message || '操作失败，请重试');
    }
  };

  const handleDelete = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!window.confirm(`确定要删除商品"${product?.name}"吗？`)) return;
    try {
      const res = await productApi.delete(productId);
      if (res.success) {
        setProducts(products.filter(p => p.id !== productId));
        alert('删除成功！');
      } else {
        alert(res.message || '删除失败');
      }
    } catch (err) {
      alert(err.message || '删除失败，请重试');
    }
  };

  const handleInventory = (product) => {
    setSelectedProduct(product);
    setShowInventoryDialog(true);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const rows = filteredProducts.map(p => `
      <tr>
        <td>${p.barcode || '-'}</td>
        <td>${p.name}</td>
        <td>${p.category_name || '-'}</td>
        <td>$${parseFloat(p.sale_price || 0).toFixed(2)}</td>
        <td>${p.vip_price ? '$' + parseFloat(p.vip_price).toFixed(2) : '-'}</td>
        <td>${p.box_quantity ? `1箱=${p.box_quantity}个` : '-'}</td>
        <td>${p.box_price ? '$' + parseFloat(p.box_price).toFixed(2) : '-'}</td>
        <td>${p.stock || 0}</td>
        <td>${(p.stock || 0) === 0 ? '缺货' : (p.stock || 0) <= (p.min_stock || 0) ? '库存不足' : '有货'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html><head><title>商品列表</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
        h2 { margin-bottom: 8px; }
        p { color: #666; margin-bottom: 16px; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f3f4f6; padding: 6px 8px; text-align: left; border: 1px solid #e5e7eb; font-size: 11px; }
        td { padding: 5px 8px; border: 1px solid #e5e7eb; }
        tr:nth-child(even) { background: #f9fafb; }
      </style>
      </head><body>
      <h2>商品列表</h2>
      <p>共 ${filteredProducts.length} 个商品 · 打印时间：${new Date().toLocaleString('zh-CN')}</p>
      <table>
        <thead><tr>
          <th>条码</th><th>商品名称</th><th>分类</th>
          <th>零售价</th><th>VIP价</th><th>箱容</th><th>箱价</th><th>库存</th><th>状态</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {/* 头部 */}
      <div className="flex-shrink-0 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t.products}</h2>
          <p className="text-xs text-gray-500 mt-1">
            共 {filteredProducts.length} 个商品
            {!isHQ && <span className="ml-2 text-orange-500 text-xs">（只读，商品由总店管理）</span>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <select value={filterParent} onChange={e => handleParentFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
            <option value="">全部大类</option>
            {categoryTree.map(p => (
              <option key={p.id} value={p.id}>{getCategoryName(p)}</option>
            ))}
          </select>

          {filterParent && (
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="">全部子分类</option>
              {currentFilterChildren.map(c => (
                <option key={c.id} value={c.id}>{getCategoryName(c)}</option>
              ))}
            </select>
          )}

          <select value={filterStock} onChange={e => setFilterStock(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
            <option value="">全部库存</option>
            <option value="in">有货</option>
            <option value="low">库存不足</option>
            <option value="out">缺货</option>
          </select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="搜索商品..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-full md:w-48" />
          </div>

          {isHQ && (
            <button onClick={handleAdd}
              className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center">
              <Plus className="w-4 h-4 mr-1" />{t.addProduct}
            </button>
          )}

          <button onClick={handlePrint}
            className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-gray-200 flex items-center">
            <Printer className="w-4 h-4 mr-1" />打印
          </button>
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}<button onClick={loadProducts} className="ml-4 underline">重试</button>
        </div>
      )}

      <div className="flex-1 min-h-0 bg-white rounded-xl shadow flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <ProductTable
              products={filteredProducts}
              onEdit={isHQ ? handleEdit : null}
              onDelete={isHQ ? handleDelete : null}
              onInventory={handleInventory}
              isReadOnly={!isHQ}
              categoryTree={categoryTree}
            />
          </div>
        )}
      </div>

      {showDialog && isHQ && (
        <ProductDialog
          product={editingProduct}
          onClose={() => { setShowDialog(false); setEditingProduct(null); }}
          onSubmit={handleSubmit}
        />
      )}

      {showInventoryDialog && selectedProduct && (
        <InventoryDialog
          product={selectedProduct}
          onClose={() => { setShowInventoryDialog(false); setSelectedProduct(null); }}
          onSuccess={loadProducts}
        />
      )}
    </div>
  );
};

export default ProductsPage;